import * as msal from '@azure/msal-angularjs';

import * as angular from 'angular';
import * as moment from 'moment';
import * as toastr from 'toastr';
import ngRedux from 'ng-redux';

import { HotkeysProvider } from 'angular-hotkeys';

import * as angularVsRepeat from 'ng-vs-repeat';
import * as ngAnimate from 'angular-animate';
import * as ngSanitize from 'angular-sanitize';

import { polyfill } from 'es6-object-assign';

import actionCreators from './stateManagement/actionCreators.module';
import dispatchers from './stateManagement/dispatchers.module';
import filters from './filters/filters.module';
import directives from './directives/directive.module';

import * as StateErrors from './common/StateErrors';
import * as wtFramework from './wtFramework';

import { controlModule } from './controls/controls.module';
import { componentModule } from './components/component.module';
import { helperModule } from './common/helpers/helpers.module';
import { serviceModule } from './services/services.module';
import { widgetModuleName } from './widgets/widgetModule';
import { statesModuleName } from './states/states.module';
import { configureToastr } from './toastrConfig';
import { httpConfig } from './httpConfig';
import { loadingBarConfig } from './loadingBarConfig';
import { markedConfig } from './marked.config';
import { reduxConfig } from './redux.config';
import { routeConfig } from './routeConfig';
import { textAngularConfig } from './textAngularConfig';
import { translateConfig } from './translateConfig';
import { xEditableConfig } from './xeditableConfig';
import { wtConfig, wtConfigProvider } from './wtConfigProvider';

import { IRootScope } from './IRootScope';
import { IConfigurationService, IKeycloakService, IStoreService } from './services';
import { msalConfig } from './msalConfig';
polyfill();

export interface IApp extends angular.IModule { }

export const app: IApp = angular
    .module('app', [
        // Angular modules
        ngAnimate, // animations
        ngSanitize, // sanitize
        'ngMessages', // sanitize
        'pascalprecht.translate', // Angular.translate
        ngRedux,
        // Custom modules
        wtFramework.name,
        widgetModuleName,
        serviceModule,
        helperModule,
        componentModule,
        controlModule,
        statesModuleName,
        actionCreators,
        dispatchers,
        filters,
        directives,
        // 3rd Party Modules
        'ui.bootstrap', // ui-bootstrap (ex: carousel, pagination, dialog)
        'ui.router', // routing
        'ui.router.state.events', // state events polyfill
        'gridster', // gridster (drag&drop resizable dashboard)
        'LocalStorageModule', // local storage
        'angular-loading-bar', // loadingbar
        'Chronicle', // chronicle
        'cfp.hotkeys', // hotkeys
        'xeditable', // inline form/tables edition
        'ngFileUpload',
        'ngCookies',
        'ui.codemirror',
        'hc.marked',
        'ui.select',
        'angular-quartz-cron',
        'ng-sortable',
        'textAngular',
        'daterangepicker',
        angularVsRepeat,
        'simplemde',
        'ui-leaflet'
    ])
    .config(httpConfig)
    .config(loadingBarConfig)
    .config(routeConfig)
    // .config(statesConfig)
    .config(translateConfig)
    //  This is to be applied with angular 1.6
    .config([
        '$qProvider',
        ($qProvider) => {
            $qProvider.errorOnUnhandledRejections(false);
        }
    ])
    .config(reduxConfig)
    .config(markedConfig)
    .config(textAngularConfig)
    .provider(wtConfig, wtConfigProvider as any)
    .constant('moment', moment)
    .constant('clientId', 'ngApp')
    .config(msalConfig)
    .run(xEditableConfig)
    .run(() => {
        configureToastr();
    });

(window as any).wtFramework = wtFramework;

try {
    // Execute bootstrapping code and any dependencies.
    app.run([
        '$rootScope',
        '$state',
        '$translate',
        'configurationService',
        'storeService',
        'hotkeys',
        'keycloakService',
        (
            $rootScope: IRootScope,
            $state: angular.ui.IStateService,
            $translate: angular.translate.ITranslateService,
            configurationService: IConfigurationService,
            storeService: IStoreService,
            hotkeys: HotkeysProvider,
        ) => {
            // load Config.json
            configurationService.loadData();
            moment.locale($translate.use());

            // Configure Hotkeys
            const states = $state.get();
            const filtered = states
                .filter((s) => !!s?.data)
                .map((s) => s.data)
                .filter((data) => data.hotkey && data.displayName);

            filtered.forEach((state) => {
                const { data, name } = state;
                const hotkeyCombo = data.hotkey;
                const description = $translate.instant(data.displayName);
                const callback = () => {
                    $state.go(name);
                };
                hotkeys.add({ combo: hotkeyCombo, description: description, callback: callback });
            });

            // page protection
            $rootScope.$on('$stateChangeError', (event, toState: angular.ui.IState, toParams, fromState: angular.ui.IState, fromParams, error) => {
                if (error.detail === StateErrors.notAuthenticated) {
                    $state.go('login');
                } else if (error.detail === StateErrors.invalidLicense) {
                    $state.go('administration');
                } else if (error.detail === StateErrors.notConnected) {
                    $state.go('workspace');
                } else if (error.detail === StateErrors.notAuthorized) {
                    toastr.warning($translate.instant('You do not have access to this page'));
                } else {
                    $state.go('notFound');
                }
            });
            //configure charting logger
            wtFramework.LoggerFactory.forceLevel(2);
            storeService.dispatchers().authentication.fillAuthData();
        }
    ]);

    // Bootstrap Angular after Keycloak initialization if enabled
    angular.element(document).ready(() => {
        // Load config.json to check if Keycloak is enabled
        fetch('config.json')
            .then(response => response.json())
            .then((config) => {
                if (config?.keycloakEnabled) {                    
                    // Create minimal injector to get Keycloak service
                    const injector = angular.injector(['ng', serviceModule]);
                    const keycloakService = injector.get<IKeycloakService>('keycloakService');
                    const $http = injector.get<angular.IHttpService>('$http');
                    const $q = injector.get<angular.IQService>('$q');
                    
                    // Get Keycloak config from API
                    const apiUrl = config.apiUrl;
                    $http.get(apiUrl + 'api/v1/ApiInformation/authenticationInformation')
                        .then((response: any) => {
                            const keycloakConfig = {
                                clientId: response.data.keycloakClientIds.keycloak_client_id_angular,
                                url: response.data.keycloakRealmBaseUrl,
                                realm: response.data.keycloakRealmName
                            };
                            
                            if (keycloakConfig?.clientId && keycloakConfig?.url && keycloakConfig?.realm) {
                                return keycloakService.initializeKeycloak(keycloakConfig);
                            }
                            return $q.when(false);
                        })
                        .then((authenticated) => {
                            angular.bootstrap(document.body, ['app']);
                        })
                        .catch((error) => {
                            angular.bootstrap(document.body, ['app']);
                        });
                } else {
                    angular.bootstrap(document.body, ['app']);
                }
            })
            .catch((error) => {
                angular.bootstrap(document.body, ['app']);
            });
    });
} catch (e) {
    console.log(e);
}
